import { FC, ReactNode, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";
import styled from "@mui/material/styles/styled";
import { keyframes } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import { useNavigate } from "react-router-dom";

const NAVY = "#16305C";
const NAVY_DEEP = "#0F2445";
const GOLD = "#F5A623";
const GOLD_DARK = "#B9770C";
const CORAL = "#C0504D";
const SKY = "#DCEEF9";

const REQUEST_DOCS_VIDEO_SRC = "https://embed.app.guidde.com/playbooks/mapt9JktfazxWZSrbEpbpF?mode=videoOnly";

const NON_CIVIL_FILING_MANUAL_SRC = "assets/pdf/I2File-User_Manual_Criminal_Dupage.pdf";

const STEPS = [
  { label: "Get started", icon: <InfoOutlinedIcon sx={{ fontSize: 20 }} /> },
  { label: "Existing account", icon: <ShieldOutlinedIcon sx={{ fontSize: 20 }} /> },
  { label: "Choose account", icon: <HowToRegRoundedIcon sx={{ fontSize: 20 }} /> },
];

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const softPop = keyframes`
  0%   { opacity: 0; transform: scale(.92) translateY(10px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

const iconPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(22,48,92,.35); }
  70%  { box-shadow: 0 0 0 8px rgba(22,48,92,0); }
  100% { box-shadow: 0 0 0 0 rgba(22,48,92,0); }
`;

const BootstrapDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== "widen",
})<{ widen?: boolean }>(({ theme, widen }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2.25, 3), overflowY: "auto", flex: "1 1 auto", minHeight: 0 },
  "& .MuiDialogTitle-root": { padding: theme.spacing(1.75, 3), flexShrink: 0 },
  "& .MuiDialog-paper": {
    maxWidth: widen ? "1000px" : "760px",
    maxHeight: "calc(100% - 32px)",
    width: "100%",
    borderRadius: 20,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    transition: "max-width .3s cubic-bezier(.4,0,.2,1)",
    boxShadow: "0 24px 60px rgba(15,36,69,.22)",
    animation: `${softPop} .38s cubic-bezier(.34,1.56,.64,1)`,
  },
}));

const BrandConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 20 },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    backgroundImage: `linear-gradient(90deg, ${NAVY}, ${GOLD})`,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    backgroundColor: GOLD,
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    borderRadius: 3,
    backgroundColor: "#E1E6EF",
  },
}));

const StepIconRoot = styled("div")<{ ownerState: { active?: boolean; completed?: boolean } }>(
  ({ ownerState }) => ({
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#B0B9C7",
    backgroundColor: "#fff",
    border: "2px solid #E1E6EF",
    transition: "all .2s ease",
    ...(ownerState.active && {
      backgroundColor: NAVY,
      color: "#fff",
      border: `2px solid ${NAVY}`,
      boxShadow: `0 0 0 4px ${SKY}`,
      animation: `${iconPulse} 1.6s ease-out`,
    }),
    ...(ownerState.completed && {
      backgroundColor: GOLD,
      color: "#fff",
      border: `2px solid ${GOLD}`,
      animation: `${softPop} .35s cubic-bezier(.34,1.56,.64,1)`,
    }),
  })
);

function BrandStepIcon(props: StepIconProps) {
  const { active, completed, icon } = props;
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? (
        <CheckRoundedIcon sx={{ fontSize: 22 }} />
      ) : (
        STEPS[Number(icon) - 1].icon
      )}
    </StepIconRoot>
  );
}

interface ActionTileProps {
  icon: ReactNode;
  label: string;
  sub: string;
  href?: string;
  external?: boolean;
  onClick?: () => void;
  trailing?: ReactNode;
  highlight?: boolean;
  delay?: number;
  "aria-expanded"?: boolean;
  "aria-controls"?: string;
}

const ActionTile: FC<ActionTileProps> = ({
  icon, label, sub, href, external, onClick, trailing, highlight, delay = 0, ...aria
}) => (
  <Box
    component={href ? "a" : "button"}
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    onClick={onClick}
    {...aria}
    sx={{
      position: "relative",
      textAlign: "left", cursor: "pointer", width: "100%", height: "100%", minWidth: 0,
      boxSizing: "border-box",
      display: "flex", alignItems: "flex-start", gap: 1.25,
      p: 1.4,
      border: highlight ? `1px solid #EED9A8` : "1px solid #E9ECF2",
      borderRadius: 3,
      bgcolor: highlight ? "#FDFAF3" : "#fff",
      color: "inherit", textDecoration: "none",
      transition: "transform .18s cubic-bezier(.34,1.56,.64,1), box-shadow .18s ease, border-color .18s ease, background-color .18s ease",
      font: "inherit",
      opacity: 0,
      animation: `${fadeSlideUp} .42s cubic-bezier(.22,1,.36,1) ${delay}ms forwards`,
      boxShadow: "0 1px 3px rgba(16,36,69,.05)",
      "&:hover": {
        borderColor: highlight ? GOLD_DARK : NAVY,
        bgcolor: highlight ? "#FBF3E1" : "#FAFBFD",
        transform: "translateY(-2px)",
        boxShadow: "0 8px 18px rgba(22,48,92,.09)",
      },
      "&:active": { transform: "translateY(-1px)" },
    }}
  >
    <Box sx={{
      width: 36, height: 36, borderRadius: 2, flexShrink: 0,
      bgcolor: highlight ? "#FBEBC9" : SKY, color: highlight ? GOLD_DARK : NAVY,
      display: "flex", alignItems: "center", justifyContent: "center",
      "& svg": { fontSize: 19 },
    }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: NAVY, lineHeight: 1.25 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, color: "#5A6472", fontWeight: 500, lineHeight: 1.35 }}>
        {sub}
      </Typography>
    </Box>
    <Box sx={{ flexShrink: 0, mt: 0.25 }}>
      {trailing ?? <ChevronRightRoundedIcon sx={{ fontSize: 20, color: "#B4BCC8" }} />}
    </Box>
  </Box>
);

interface TypeCardProps {
  icon: ReactNode;
  title: string;
  bestFor: string;
  points: string[];
  cta: string;
  onClick: () => void;
  delay?: number;
}

const TypeCard: FC<TypeCardProps> = ({ icon, title, bestFor, points, cta, onClick, delay = 0 }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 3, borderColor: "#E1E6EF", height: "100%",
      transition: "transform .2s cubic-bezier(.34,1.56,.64,1), box-shadow .2s ease, border-color .2s ease",
      opacity: 0,
      animation: `${fadeSlideUp} .45s cubic-bezier(.22,1,.36,1) ${delay}ms forwards`,
      "&:hover": { borderColor: NAVY, transform: "translateY(-4px)", boxShadow: "0 14px 32px rgba(22,48,92,.14)" },
    }}
  >
    <CardActionArea onClick={onClick} sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
        <Box sx={{ width: 48, height: 48, borderRadius: 2.5, flexShrink: 0, bgcolor: SKY, color: NAVY, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {icon}
        </Box>
        <Typography sx={{ fontFamily: "Lora, Georgia, serif", fontWeight: 600, fontSize: 20, color: NAVY }}>{title}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: "#5A6472", mb: 2 }}>
        <b style={{ color: "#2B333F" }}>Best for:</b> {bestFor}
      </Typography>
      <Stack spacing={1.1} sx={{ mb: 3 }}>
        {points.map((p) => (
          <Stack key={p} direction="row" spacing={1}>
            <CheckRoundedIcon sx={{ fontSize: 18, color: GOLD, mt: "2px", flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: "#2B333F" }}>{p}</Typography>
          </Stack>
        ))}
      </Stack>
      <Button component="div" variant="contained" endIcon={<ArrowForwardRoundedIcon />}
        sx={{ mt: "auto", textTransform: "none", fontWeight: 600, borderRadius: 2, py: 1, bgcolor: NAVY, "&:hover": { bgcolor: NAVY_DEEP } }}>
        {cta}
      </Button>
    </CardActionArea>
  </Card>
);

const REQUEST_DOCS_STEPS = [
  <>Sign in to I2File and open the <b>Dashboard</b>.</>,
  <>Select <b>Document Request</b> from the dashboard or the left sidebar.</>,
  <>Choose the option for <b>DuPage, Will and Kendall</b> jurisdictions.</>,
  <>Follow the on-screen prompts to request documents or to pay for and download previously requested documents.</>,
];

const RequestDocsView: FC = () => {
  const [videoError, setVideoError] = useState(false);
  const hasVideo = !!REQUEST_DOCS_VIDEO_SRC && !videoError;

  return (
  <Box sx={{ animation: `${fadeSlideUp} .4s cubic-bezier(.22,1,.36,1)` }}>
    <Box
      sx={{
        display: "flex", gap: 2, alignItems: "center",
        p: { xs: 2, sm: 2.5 }, mb: 3, borderRadius: 3,
        background: `linear-gradient(135deg, ${SKY} 0%, #EFF6FD 100%)`,
      }}
    >
      <Box sx={{
        width: 50, height: 50, borderRadius: 2.5, flexShrink: 0,
        bgcolor: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 6px 16px rgba(22,48,92,.25)",
      }}>
        <FolderOpenRoundedIcon />
      </Box>
      <Box>
        <Typography sx={{ fontFamily: "Lora, Georgia, serif", fontWeight: 600, fontSize: { xs: 18, sm: 20 }, color: NAVY, mb: 0.3 }}>
          Request Documents
        </Typography>
        <Typography variant="body2" sx={{ color: "#42506A" }}>
          For <b style={{ color: "#2B333F" }}>DuPage</b>, <b style={{ color: "#2B333F" }}>Will</b> &amp; <b style={{ color: "#2B333F" }}>Kendall</b> jurisdictions — after you create an account and sign in.
        </Typography>
      </Box>
    </Box>

    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.05fr 1fr" }, gap: { xs: 3, md: 3.5 }, alignItems: "start" }}>
      <Box sx={{ position: "relative" }}>
        <Box sx={{ position: "absolute", left: 15, top: 16, bottom: 16, width: "2px", bgcolor: "#E1E6EF", zIndex: 0 }} />
        <Stack spacing={3}>
          {REQUEST_DOCS_STEPS.map((step, i) => (
            <Box
              key={i}
              sx={{
                position: "relative", zIndex: 1,
                display: "grid", gridTemplateColumns: "32px 1fr", columnGap: 1.75, alignItems: "start",
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: 13.5, color: "#fff",
                bgcolor: NAVY,
                boxShadow: "0 3px 8px rgba(22,48,92,.22)",
              }}>
                {i + 1}
              </Box>
              <Typography variant="body2" sx={{ color: "#2B333F", pt: "5px", fontSize: 14.5, lineHeight: 1.5 }}>
                {step}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      <Box>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".7px", textTransform: "uppercase", color: "#98A1AE", mb: 1.25 }}>
          Watch how it works
        </Typography>
        <Box
          sx={{
            position: "relative", width: "100%", aspectRatio: "16 / 9",
            borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(160deg, ${SKY} 0%, #F6F8FB 60%)`,
            border: `1px solid #D7E4F2`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {hasVideo ? (
            <Box
              component="iframe"
              src={REQUEST_DOCS_VIDEO_SRC}
              title="How to request documents"
              allow="fullscreen"
              allowFullScreen
              onError={() => setVideoError(true)}
              sx={{ width: "100%", height: "100%", border: 0, display: "block" }}
            />
          ) : (
            <Stack alignItems="center" spacing={1.25} sx={{ textAlign: "center", px: 3 }}>
              <Box sx={{
                width: 62, height: 62, borderRadius: "50%",
                bgcolor: GOLD, display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 8px 20px rgba(245,166,35,.4)",
              }}>
                <PlayCircleRoundedIcon sx={{ fontSize: 42, color: "#fff" }} />
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: NAVY }}>
                Walkthrough video coming soon
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "#5A6472" }}>
                A step-by-step video on requesting documents will appear here.
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  </Box>
  );
};

const NON_CIVIL_FILING_STEPS = [
  {
    title: "Connect to Classic i2File",
    body: <>Sign in to i2File and click the <b>I2F icon</b> at the top of the Dashboard to open Classic i2File.</>,
  },
  {
    title: "Select Your County",
    body: <>Choose the appropriate county/jurisdiction — <b>DuPage</b>, <b>Kendall</b>, or <b>McHenry</b> — from the Jurisdiction dropdown.</>,
  },
  {
    title: "Enter Required Information",
    body: <>Enter the required attorney information and click <b>Select Jurisdiction</b>. Self-Represented (Pro Se) users can select the appropriate jurisdiction and continue.</>,
  },
  {
    title: "Choose What You Want to Do",
    body: <>Select <b>New Case Filing</b>, <b>File Documents in Existing Case</b>, or <b>Request Document Copy</b>, based on the options available for the selected jurisdiction.</>,
  },
  {
    title: "Review Status & Payments",
    body: <>Select <b>Review Status of your Document Filings and Copy Requests</b> to view approved transactions and any transactions that require payment.</>,
  },
];

const NonCivilFilingView: FC = () => (
  <Box sx={{ animation: `${fadeSlideUp} .4s cubic-bezier(.22,1,.36,1)` }}>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}
      sx={{
        border: "1px solid #EED9A8", bgcolor: "#FDFAF3", borderRadius: 3, p: 1.5, mb: 1.75,
      }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2.25, flexShrink: 0,
        bgcolor: "#FBEBC9", color: GOLD_DARK, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <GavelRoundedIcon />
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 15, color: NAVY }}>
          Filing Criminal, DUI, or Traffic cases?
        </Typography>
        <Typography variant="body2" sx={{ color: "#5A6472", fontWeight: 500 }}>
          Criminal, DUI &amp; Traffic Filing Guide for DuPage, Kendall &amp; McHenry Counties
        </Typography>
      </Box>
      <Button
        component="a"
        href={NON_CIVIL_FILING_MANUAL_SRC}
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: "none", fontWeight: 600, borderRadius: 2.5, px: 2.5, py: 1, whiteSpace: "nowrap",
          color: GOLD_DARK, border: `1px solid #EED9A8`, bgcolor: "#fff",
          "&:hover": { bgcolor: "#FBEBC9", borderColor: GOLD_DARK },
        }}
      >
        Open PDF Guide
      </Button>
    </Stack>

    <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".7px", textTransform: "uppercase", color: "#98A1AE", mb: 1.75 }}>
      File Criminal, DUI &amp; Traffic Cases — DuPage, Kendall and McHenry
    </Typography>

    <Box sx={{ position: "relative" }}>
      <Box sx={{ position: "absolute", left: 15, top: 16, bottom: 16, width: "2px", bgcolor: "#E1E6EF", zIndex: 0 }} />
      <Stack spacing={2.5}>
        {NON_CIVIL_FILING_STEPS.map((step, i) => (
          <Box
            key={i}
            sx={{
              position: "relative", zIndex: 1,
              display: "grid", gridTemplateColumns: "32px 1fr", columnGap: 1.75, alignItems: "start",
            }}
          >
            <Box sx={{
              width: 32, height: 32, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: 13.5, color: "#fff",
              bgcolor: NAVY,
              boxShadow: "0 3px 8px rgba(22,48,92,.22)",
            }}>
              {i + 1}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: NAVY, mb: 0.25 }}>
                {step.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "#2B333F", fontSize: 14, lineHeight: 1.5 }}>
                {step.body}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  </Box>
);

interface RegistrationFlowModalProps {
  open: boolean;
  onClose: () => void;
  onGoToLogin: () => void;
}

const RegistrationFlowModal: FC<RegistrationFlowModalProps> = ({
  open,
  onClose,
  onGoToLogin,
}) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showRequestDocs, setShowRequestDocs] = useState(false);
  const [showNonCivilFiling, setShowNonCivilFiling] = useState(false);
  const showSubView = showRequestDocs || showNonCivilFiling;

  useEffect(() => {
    if (!open) {
      setShowRequestDocs(false);
      setShowNonCivilFiling(false);
      setActiveStep(0);
    }
  }, [open]);

  const createFirmAccount = () => { onClose(); navigate("/register/firmUser"); };
  const createSelfAccount = () => { onClose(); navigate("/register/independentuser"); };
  const goToLogin = () => { onClose(); onGoToLogin(); };

  const handleBack = () => {
    if (showRequestDocs) { setShowRequestDocs(false); return; }
    if (showNonCivilFiling) { setShowNonCivilFiling(false); return; }
    setActiveStep((s) => Math.max(0, s - 1));
  };
  const handleContinue = () => {
    if (showRequestDocs) { setShowRequestDocs(false); setActiveStep(1); return; }
    if (showNonCivilFiling) { setShowNonCivilFiling(false); setActiveStep(1); return; }
    setActiveStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const showBack = showSubView || activeStep > 0;
  const showContinue = showSubView || activeStep < STEPS.length - 1;
  const continueLabel = !showSubView && activeStep === 1 ? "Register" : "Continue";

  return (
    <BootstrapDialog
      onClose={onClose}
      aria-labelledby="registration-flow-title"
      open={open}
      className="registration-flow-popup popup-main"
      maxWidth={showRequestDocs ? "lg" : "md"}
      widen={showRequestDocs}
      transitionDuration={{ enter: 320, exit: 200 }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(15,28,51,.55)",
            backdropFilter: "blur(3px)",
            transition: "opacity .3s ease, backdrop-filter .3s ease !important",
          },
        },
      }}
    >
      <DialogTitle
        id="registration-flow-title"
      >
        <h4>Create an Account</h4>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 14, color: "#8A93A2" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        className="popup-content registration-flow-content"
      >
        {!showSubView && (
          <Stepper activeStep={activeStep} alternativeLabel connector={<BrandConnector />} sx={{ mb: 2.5 }}>
            {STEPS.map((s) => (
              <Step key={s.label}>
                <StepLabel StepIconComponent={BrandStepIcon}
                  sx={{ "& .MuiStepLabel-label": { mt: 1, fontWeight: 500, "&.Mui-active": { color: NAVY }, "&.Mui-completed": { color: GOLD_DARK } } }}>
                  {s.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {showRequestDocs && <RequestDocsView />}

        {showNonCivilFiling && <NonCivilFilingView />}

        {!showSubView && activeStep === 0 && (
          <Box sx={{ animation: `${fadeSlideUp} .4s cubic-bezier(.22,1,.36,1)` }}>
            <Box
              sx={{
                display: "flex", gap: 1.75, alignItems: "flex-start",
                p: 2, mb: 1.75, borderRadius: 3.5,
                background: `linear-gradient(135deg, ${SKY} 0%, #EFF6FD 100%)`,
                boxShadow: "0 8px 24px rgba(22,72,140,.08)",
              }}
            >
              <Box sx={{
                width: 40, height: 40, borderRadius: 2.5, flexShrink: 0,
                bgcolor: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 6px 14px rgba(22,48,92,.28)",
              }}>
                <HowToRegRoundedIcon />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: "Lora, Georgia, serif", fontWeight: 600, fontSize: 17, color: NAVY, mb: 0.3 }}>
                  Before you begin
                </Typography>
                <Typography variant="body2" sx={{ color: "#42506A" }}>
                  To <b>request documents</b>, <b>file into an existing case</b>, or <b>file a new case</b>,
                  you must first create an account if you don&apos;t already have one.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25, alignItems: "stretch" }}>
              <ActionTile
                icon={<FolderOpenRoundedIcon />}
                label="Request Documents"
                sub="DuPage · Will · Kendall"
                onClick={() => setShowRequestDocs(true)}
                highlight
                delay={60}
              />
              <ActionTile
                icon={<GavelRoundedIcon />}
                label="Non Civil Filing"
                sub="Criminal, DUI, and Traffic Filings"
                onClick={() => setShowNonCivilFiling(true)}
                highlight
                delay={110}
              />
              <ActionTile
                icon={<DescriptionRoundedIcon />}
                label="File Into Existing Case"
                sub="Add a filing to an existing case"
                onClick={() => setActiveStep(1)}
                highlight
                delay={160}
              />
              <ActionTile
                icon={<NoteAddRoundedIcon />}
                label="File a New Case"
                sub="Start a brand-new filing"
                onClick={() => setActiveStep(1)}
                highlight
                delay={210}
              />
            </Box>
          </Box>
        )}

        {!showSubView && activeStep === 1 && (
          <Box sx={{ animation: `${fadeSlideUp} .4s cubic-bezier(.22,1,.36,1)` }}>
            <Stack direction="row" spacing={1.75} alignItems="flex-start" mb={2.5}>
              <Box sx={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0, bgcolor: SKY, color: NAVY, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ShieldOutlinedIcon />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: "Lora, Georgia, serif", fontWeight: 600, fontSize: 19, color: NAVY, mb: 0.5 }}>
                  Already have an account?
                </Typography>
                <Typography variant="body2" sx={{ color: "#5A6472" }}>
                  Please <b style={{ color: "#2B333F" }}>do not create a new account</b> if you or your firm is already
                  registered with the Odyssey eFileIL system through a certified e-filing service provider.
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.75} alignItems={{ sm: "center" }}
              sx={{ bgcolor: "#F7F9FC", border: "1px solid #E9ECF2", borderRadius: 3, p: 2.25, mb: 2.5 }}>
              <Button variant="contained" startIcon={<LoginRoundedIcon />} onClick={goToLogin}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2.5, px: 2.5, py: 1, bgcolor: NAVY, whiteSpace: "nowrap", boxShadow: "0 6px 16px rgba(22,48,92,.24)", "&:hover": { bgcolor: NAVY_DEEP } }}>
                Login
              </Button>
              <Typography variant="body2" sx={{ color: "#42506A" }}>
                Firm already registered? Ask your <b>Firm Administrator</b> to add you to the account.
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              <Box
                component="a"
                href="assets/pdf/I2File-User_Manual_v1.0.pdf"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.7,
                  fontSize: 13, fontWeight: 600, color: NAVY, textDecoration: "none",
                  bgcolor: SKY, borderRadius: 5, px: 1.75, py: 0.9,
                  transition: "all .15s ease",
                  "&:hover": { bgcolor: "#CBE3F5", transform: "translateY(-1px)" },
                }}
              >
                <MenuBookRoundedIcon sx={{ fontSize: 16 }} />
                View User Manual
                <OpenInNewRoundedIcon sx={{ fontSize: 13 }} />
              </Box>
              <Box
                component="a"
                href="/#/support"
                onClick={onClose}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.7,
                  fontSize: 13, fontWeight: 600, color: NAVY, textDecoration: "none",
                  bgcolor: SKY, borderRadius: 5, px: 1.75, py: 0.9,
                  transition: "all .15s ease",
                  "&:hover": { bgcolor: "#CBE3F5", transform: "translateY(-1px)" },
                }}
              >
                <SupportAgentRoundedIcon sx={{ fontSize: 16 }} />
                Help &amp; Support
              </Box>
            </Stack>
          </Box>
        )}

        {!showSubView && activeStep === 2 && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5, animation: `${fadeSlideUp} .4s cubic-bezier(.22,1,.36,1)` }}>
            <TypeCard
              icon={<AccountBalanceRoundedIcon />}
              title="Firm Account"
              bestFor="attorneys, law firms with multiple filers, and solo attorneys."
              points={["Add attorneys and support staff", "Manage users associated with your firm", "Set up and maintain payment info"]}
              cta="Create Firm Account"
              onClick={createFirmAccount}
              delay={60}
            />
            <TypeCard
              icon={<PersonRoundedIcon />}
              title="Self-Represented Account"
              bestFor="Pro Se filers and individuals not associated with a law firm."
              points={["Pro Se / self-represented filers", "Independent court reporters & process servers", "Landlords, tenants & other individuals"]}
              cta="Create Self-Represented Account"
              onClick={createSelfAccount}
              delay={140}
            />
          </Box>
        )}

      </DialogContent>

      {(showBack || showContinue) && (
        <Box sx={{ flexShrink: 0, borderTop: "1px solid #EDF0F5", px: 3, py: 2 }}>
          <Stack direction="row" justifyContent={showBack ? "space-between" : "flex-end"}>
            {showBack && (
              <Button onClick={handleBack}
                sx={{ textTransform: "none", fontWeight: 600, color: NAVY, borderRadius: 2, px: 1.5, "&:hover": { bgcolor: SKY } }}>
                Back
              </Button>
            )}
            {showContinue && (
              <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />}
                onClick={handleContinue}
                sx={{
                  textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3, py: 1,
                  background: `linear-gradient(135deg, ${NAVY}, ${NAVY_DEEP})`,
                  boxShadow: "0 8px 20px rgba(22,48,92,.28)",
                  "&:hover": { background: `linear-gradient(135deg, ${NAVY_DEEP}, ${NAVY_DEEP})`, boxShadow: "0 10px 24px rgba(22,48,92,.34)" },
                }}>
                {continueLabel}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </BootstrapDialog>
  );
};

export default RegistrationFlowModal;
